import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import SignatureCanvas from "react-signature-canvas";
import { FileSignature, CheckCircle2, RotateCcw, Clock, ArrowRight } from "lucide-react";

export function ClientEstimatesList() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/estimates/my")
      .then((r) => setItems(r.data))
      .catch((err) => console.warn("Client estimates fetch failed:", err));
  }, []);
  return (
    <div data-testid="client-estimates">
      <p className="overline">Proposals</p>
      <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-2 mb-8">Estimates</h1>
      {items.length === 0 ? (
        <div className="card-pb p-12 text-center text-[#475569] dark:text-slate-400">
          No estimates yet — we'll send one your way after our consultation.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((e) => {
            const pillCls = STATUS_PILL[e.status] || "bg-slate-200 text-slate-700";
            return (
              <Link key={e.estimate_id} to={`/dashboard/estimates/${e.estimate_id}`}
                    className="card-pb p-5 flex items-center justify-between hover:border-[#C9A356] transition" data-testid={`my-est-${e.estimate_id}`}>
                <div>
                  <div className="text-[10px] font-mono text-[#94A3B8]">{e.estimate_id}</div>
                  <div className="font-medium text-[#0A2540] dark:text-white mt-0.5">{e.title}</div>
                  <div className="text-xs text-[#475569] dark:text-slate-400 mt-0.5">Sent {new Date(e.sent_at || e.created_at).toLocaleDateString()}</div>
                </div>
                <div className="text-right">
                  <div className="font-heading text-2xl font-bold text-[#0A2540] dark:text-white tabular-nums">${(e.grand_total || 0).toFixed(2)}</div>
                  <span className={`mt-1 inline-block text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full ${pillCls}`}>{e.status}</span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

const STATUS_PILL = {
  sent: "bg-blue-100 text-blue-700",
  viewed: "bg-indigo-100 text-indigo-700",
  signed: "bg-emerald-100 text-emerald-700",
  declined: "bg-red-100 text-red-700",
  expired: "bg-amber-100 text-amber-700",
  converted: "bg-[#C9A356]/20 text-[#0A2540]",
};

export function ClientEstimateDetail() {
  const { id } = useParams();
  const [est, setEst] = useState(null);
  const [signing, setSigning] = useState(false);
  const sigRef = useRef(null);

  const load = () => api.get(`/estimates/${id}`)
    .then((r) => setEst(r.data))
    .catch((err) => console.warn("Client estimate detail fetch failed:", err));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  if (!est) return <div className="text-[#475569] dark:text-slate-400">Loading…</div>;

  const sign = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) { toast.error("Please sign first"); return; }
    setSigning(true);
    try {
      const sig = sigRef.current.getCanvas().toDataURL("image/png");
      await api.post(`/estimates/my/${id}/sign`, { signature: sig });
      toast.success("Signed ✓ — our team will be in touch");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to sign");
    } finally { setSigning(false); }
  };

  const canSign = !["signed", "declined", "expired", "converted"].includes(est.status);
  const pillCls = STATUS_PILL[est.status] || "bg-slate-200 text-slate-700";

  return (
    <div className="max-w-3xl mx-auto" data-testid="client-est-detail">
      <Link to="/dashboard/estimates" className="text-sm text-[#475569] dark:text-slate-400 hover:text-[#C9A356] inline-flex items-center gap-1 mb-4">← Back to estimates</Link>

      <div className="card-pb p-6 mb-5">
        <div className="flex justify-between items-start flex-wrap gap-3">
          <div>
            <p className="overline">{est.estimate_id}</p>
            <h1 className="font-heading text-2xl text-[#0A2540] dark:text-white mt-1">{est.title}</h1>
            {est.valid_until && <div className="text-xs text-[#475569] dark:text-slate-400 mt-1 inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Valid until {new Date(est.valid_until).toLocaleDateString()}</div>}
          </div>
          <span className={`text-xs uppercase tracking-wider font-bold px-3 py-1 rounded-full ${pillCls}`}>{est.status}</span>
        </div>

        <table className="w-full text-sm mt-5">
          <thead className="text-xs uppercase tracking-wider text-[#475569] dark:text-slate-400 border-b border-[#E2E8F0] dark:border-slate-700">
            <tr><th className="py-2 text-left">Item</th><th className="py-2 text-right">Qty</th><th className="py-2 text-right">Unit</th><th className="py-2 text-right">Total</th></tr>
          </thead>
          <tbody>
            {(est.line_items || []).map((li, i) => (
              <tr key={`${li.description}-${i}`} className="border-b border-[#E2E8F0] dark:border-slate-700">
                <td className="py-2.5">{li.description}</td>
                <td className="py-2.5 text-right tabular-nums">{li.qty}</td>
                <td className="py-2.5 text-right tabular-nums">${parseFloat(li.unit_price).toFixed(2)}</td>
                <td className="py-2.5 text-right tabular-nums">${(parseFloat(li.qty) * parseFloat(li.unit_price)).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="ml-auto max-w-xs space-y-1 text-sm mt-4">
          <div className="flex justify-between text-[#475569] dark:text-slate-400"><span>Subtotal</span><span className="tabular-nums">${(est.subtotal || 0).toFixed(2)}</span></div>
          {est.discount > 0 && <div className="flex justify-between text-[#475569] dark:text-slate-400"><span>Discount</span><span className="tabular-nums">-${(est.discount || 0).toFixed(2)}</span></div>}
          <div className="flex justify-between text-[#475569] dark:text-slate-400"><span>Tax ({((est.tax_rate || 0) * 100).toFixed(0)}%)</span><span className="tabular-nums">${(est.tax_amount || 0).toFixed(2)}</span></div>
          <div className="pt-2 mt-2 border-t border-[#E2E8F0] dark:border-slate-700 flex justify-between font-bold text-[#0A2540] dark:text-white text-base">
            <span>Total</span><span className="tabular-nums">${(est.grand_total || 0).toFixed(2)}</span>
          </div>
        </div>
        {est.scope_notes && (
          <div className="mt-5 pt-4 border-t border-[#E2E8F0] dark:border-slate-700">
            <div className="text-[10px] uppercase tracking-wider font-bold text-[#475569] dark:text-slate-400 mb-1">Scope</div>
            <p className="text-sm text-[#0A2540] dark:text-white whitespace-pre-wrap">{est.scope_notes}</p>
          </div>
        )}
      </div>

      {est.status === "signed" && (
        <div className="card-pb p-6 bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800/40">
          <div className="font-medium text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-5 h-5" /> Signed by {est.signed_name} on {new Date(est.signed_at).toLocaleDateString()}
          </div>
          {est.invoice_id && (
            <Link to={`/dashboard/invoices/${est.invoice_id}`} className="btn-accent text-sm mt-4" data-testid="open-converted-invoice">
              View invoice <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          )}
        </div>
      )}

      {canSign && (
        <div className="card-pb p-6" data-testid="portal-sign-block">
          <h2 className="font-heading text-xl text-[#0A2540] dark:text-white mb-2 flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-[#C9A356]" /> Approve & sign
          </h2>
          <p className="text-sm text-[#475569] dark:text-slate-400 mb-4">Sign below to authorize this scope of work.</p>
          <div className="border-2 border-dashed border-[#C9A356]/40 rounded-md bg-white touch-none mb-2" data-testid="portal-sig-pad">
            <SignatureCanvas ref={sigRef} canvasProps={{ width: 600, height: 180, className: "w-full" }} />
          </div>
          <button onClick={() => sigRef.current?.clear()} className="text-xs text-[#475569] hover:text-[#0A2540] inline-flex items-center gap-1 mb-4">
            <RotateCcw className="w-3 h-3" /> Clear
          </button>
          <button onClick={sign} disabled={signing} className="btn-accent w-full" data-testid="portal-sign-submit">
            <CheckCircle2 className="w-4 h-4 mr-1" /> {signing ? "Signing…" : "Sign estimate"}
          </button>
        </div>
      )}
    </div>
  );
}
